// Better Auth browser client. Same origin as the api (Vite proxy in dev, @fastify/static in prod).
import { magicLinkClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';

export const authClient = createAuthClient({
  basePath: '/api/auth',
  plugins: [magicLinkClient()],
});

// Absolute callback URLs so Better Auth lands on the web origin (Vite :5174 in dev), which must be in trustedOrigins.
function webUrl(path: string): string {
  return new URL(path, window.location.origin).toString();
}

export async function sendMagicLink(email: string, callbackURL = webUrl('/'), errorCallbackURL = webUrl('/login')): Promise<void> {
  const { error } = await authClient.signIn.magicLink({ email, callbackURL, errorCallbackURL });
  if (error) throw new Error(error.message ?? 'Could not send the magic link');
}

export async function signInWithMicrosoft(callbackURL = webUrl('/')): Promise<void> {
  const { error } = await authClient.signIn.social({ provider: 'microsoft', callbackURL });
  if (error) throw new Error(error.message ?? 'Microsoft sign-in failed');
}

/**
 * App Store and Google Play reviewers only (REVIEW_LOGIN_EMAILS on the api): email and password. Anyone else gets the
 * same "Invalid email or password" as a wrong password.
 */
export async function signInWithPassword(email: string, password: string): Promise<void> {
  const { error } = await authClient.signIn.email({ email, password });
  if (error) throw new Error(error.status === 429 ? (error.message ?? 'Too many attempts. Try again later.') : 'Invalid email or password.');
}

export async function signOut(): Promise<void> {
  await authClient.signOut();
}

/**
 * Accept an emailed project invitation (Better Auth endpoint /api/auth/guest-invitation/redeem). The link's secret
 * travels in the body, never in a URL. Returns the project to open.
 */
export async function redeemGuestInvitation(body: { id: string; secret: string; email: string }): Promise<{ projectId: string }> {
  const response = await fetch('/api/auth/guest-invitation/redeem', {
    method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  });
  const result = (await response.json().catch(() => ({}))) as { projectId?: string; message?: string };
  if (!response.ok || !result.projectId) throw new Error(result.message ?? 'This invitation could not be accepted.');
  return { projectId: result.projectId };
}
